import { useState } from "react";
import toast from "react-hot-toast";
import PageShell from "../components/layout/PageShell";
import api from "../services/api";
import { getCurrentUser } from "../services/authService";

function getErrorMessage(error, fallback = "Failed to save branding.") {
    const apiError = error?.response?.data;

    if (typeof apiError === "string") return apiError;
    if (apiError?.message) return apiError.message;
    if (apiError?.title) return apiError.title;
    if (error?.message) return error.message;

    return fallback;
}

export default function BrandingPage() {
    const user = getCurrentUser();

    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState({
        logoUrl: user?.logoUrl || "",
        primaryColor: user?.primaryColor || "#4f7cff",
        domain: user?.domain || "",
        subdomain: user?.subdomain || ""
    });

    const update = (key, value) => {
        setForm(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleSave = async () => {
        if (!user?.accountId) {
            toast.error("Account information is missing. Please login again.");
            return;
        }

        try {
            setSaving(true);
            toast.loading("Saving branding settings...", { id: "branding-save" });

            await api.post(`/accounts/${user.accountId}/branding`, form);

            toast.success("Branding settings saved.", { id: "branding-save" });

            setTimeout(() => {
                window.location.reload();
            }, 600);
        } catch (error) {
            console.error(error);
            toast.error(getErrorMessage(error), { id: "branding-save" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <PageShell title="Branding">
            <div className="branding-settings-shell">
                <section className="client-settings-hero">
                    <div>
                        <span className="project-kicker">Account Branding</span>
                        <h1>Brand Identity</h1>
                        <p>
                            Update the logo, main brand color, and domain labels used across your Nexus workspace.
                        </p>

                        <div className="client-settings-summary">
                            <span>{form.logoUrl ? "Logo added" : "No logo yet"}</span>
                            <span>{form.primaryColor || "Default color"}</span>
                            <span>{form.domain || form.subdomain || "No domain set"}</span>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="settings-save-btn"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save Branding"}
                    </button>
                </section>

                <section className="client-settings-layout">
                    <div className="client-settings-main">
                        <div className="project-panel client-setting-panel">
                            <span className="project-kicker">Visual Identity</span>
                            <h2>Logo & Color</h2>
                            <p className="settings-muted">
                                These settings help personalize the CRM and future website branding.
                            </p>

                            <div className="settings-field-grid">
                                <label>
                                    <span>Logo Image URL</span>
                                    <input
                                        value={form.logoUrl}
                                        onChange={(e) => update("logoUrl", e.target.value)}
                                        placeholder="https://..."
                                    />
                                    <small>Add a public logo image URL. Leave empty to use the default Nexus mark.</small>
                                </label>

                                <label>
                                    <span>Main Brand Color</span>
                                    <div className="branding-color-row">
                                        <input
                                            type="color"
                                            value={form.primaryColor || "#4f7cff"}
                                            onChange={(e) => update("primaryColor", e.target.value)}
                                        />

                                        <input
                                            value={form.primaryColor || ""}
                                            onChange={(e) => update("primaryColor", e.target.value)}
                                            placeholder="#4f7cff"
                                        />
                                    </div>
                                    <small>Used for primary highlights where branding support is enabled.</small>
                                </label>
                            </div>

                            <div className="branding-preview-card">
                                <div className="branding-preview-logo" style={{ background: form.primaryColor || "#4f7cff" }}>
                                    {form.logoUrl ? (
                                        <img src={form.logoUrl} alt="Brand preview" />
                                    ) : (
                                        <span>SN</span>
                                    )}
                                </div>

                                <div>
                                    <strong>{user?.name || "Your Workspace"}</strong>
                                    <span>Preview of your workspace brand identity.</span>
                                </div>
                            </div>
                        </div>

                        <div className="project-panel client-setting-panel">
                            <span className="project-kicker">Domain Labels</span>
                            <h2>Domain & Subdomain</h2>
                            <p className="settings-muted">
                                Store your preferred domain labels. DNS connection and custom-domain verification can be added later.
                            </p>

                            <div className="settings-field-grid">
                                <label>
                                    <span>Custom Domain</span>
                                    <input
                                        value={form.domain}
                                        onChange={(e) => update("domain", e.target.value)}
                                        placeholder="example.com"
                                    />
                                    <small>Your preferred main business domain.</small>
                                </label>

                                <label>
                                    <span>Subdomain</span>
                                    <input
                                        value={form.subdomain}
                                        onChange={(e) => update("subdomain", e.target.value)}
                                        placeholder="academy"
                                    />
                                    <small>A short workspace or website label, for example academy or urban-thread.</small>
                                </label>
                            </div>
                        </div>
                    </div>

                    <aside className="client-settings-side">
                        <div className="client-setting-help">
                            <strong>What changes after saving?</strong>
                            <span>Your account branding is saved. Some UI values refresh after reload.</span>
                        </div>

                        <div className="client-setting-help">
                            <strong>Will this connect my domain?</strong>
                            <span>No. This stores branding/domain preferences only. DNS verification can be added later.</span>
                        </div>

                        <div className="client-setting-help">
                            <strong>Recommended beta setup</strong>
                            <span>Add logo URL and primary color. Keep domain blank until custom-domain flow is ready.</span>
                        </div>
                    </aside>
                </section>
            </div>
        </PageShell>
    );
}